import {
  OEMCONFIG_ACTION,
  OEMCONFIG_RECEIVER,
  OEMCONFIG_SYNC_MAX_ATTEMPTS,
  OEMCONFIG_SYNC_TIMEOUT_MS,
  PHASE_A_SETTINGS,
  ROTATION_LOCK_SETTINGS,
  STAY_AWAKE_SETTING,
} from '@mxa-argos/modules/forja/constants/forja.constants';
import type { ForjaStep } from '@mxa-argos/modules/forja/interfaces/forja-step.interface';
import type { ForjaDeviceSession } from '@mxa-argos/modules/forja/lib/engine/forja-device-session';
import { flashlightGestureStep } from '@mxa-argos/modules/forja/lib/provisioning/steps/flashlight-gesture.step';
import { kioskReleaseStep } from '@mxa-argos/modules/forja/lib/provisioning/steps/kiosk-release.step';
import { wifiOnStep } from '@mxa-argos/modules/forja/lib/provisioning/steps/wifi.steps';

export function settingsStep(namespace: string, key: string, target: string): ForjaStep {
  return {
    name: `settings ${namespace}/${key}=${target}`,
    verify: async (device) => (await device.shell('settings', 'get', namespace, key)) === target,
    apply: async (device) => {
      await device.shell('settings', 'put', namespace, key, target);
    },
    verifyTimeoutMs: 5_000,
  };
}

export function phaseASteps(): ForjaStep[] {
  const oemConfigSync: ForjaStep = {
    name: 'OEMConfig sync (locale es)',
    verify: verifyLocaleSpanish,
    apply: triggerOEMConfigSync,
    verifyTimeoutMs: OEMCONFIG_SYNC_TIMEOUT_MS,
    maxAttempts: OEMCONFIG_SYNC_MAX_ATTEMPTS,
  };

  return [
    wifiOnStep(),
    settingsStep(...STAY_AWAKE_SETTING),
    ...ROTATION_LOCK_SETTINGS.map(([namespace, key, target]) =>
      settingsStep(namespace, key, target),
    ),
    kioskReleaseStep(),
    oemConfigSync,
    ...PHASE_A_SETTINGS.map(([namespace, key, target]) => settingsStep(namespace, key, target)),
    flashlightGestureStep(),
  ];
}

async function verifyLocaleSpanish(device: ForjaDeviceSession): Promise<boolean> {
  const persisted = await device.shell('getprop', 'persist.sys.locale');
  const locale = persisted || (await device.shell('getprop', 'ro.product.locale'));
  return locale.startsWith('es');
}

async function triggerOEMConfigSync(device: ForjaDeviceSession): Promise<void> {
  await device.shell('am', 'broadcast', '-n', OEMCONFIG_RECEIVER, '-a', OEMCONFIG_ACTION);
}
